// src/App.js — v5.0 — Supervision des passerelles (secours radio)
// Remplace la surveillance compteur (tension/courant/fréquence/puissance)
// par l'état des passerelles reçu par radio via la station de base.
import React, { useState, useCallback, useRef, useEffect } from 'react';
import AppHeader from './components/AppHeader';
import { useWebSocket, CONNECTION_STATE } from './hooks/useWebSocket';
import { parsePasserelle, formatTime } from './utils/parser';

const MAX_PACKETS = 200;
let uidCounter = 0;

function useUptime() {
  const start = useRef(Date.now());
  const [d, setD] = useState('00:00:00');
  useEffect(() => {
    const t = setInterval(() => {
      const s = Math.floor((Date.now() - start.current) / 1000);
      setD([Math.floor(s/3600), Math.floor((s%3600)/60), s%60].map(n => String(n).padStart(2,'0')).join(':'));
    }, 1000);
    return () => clearInterval(t);
  }, []);
  return d;
}

function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  return now;
}

const C = { or:'#f5a623', bl:'#29b6f6', vt:'#00e676', rg:'#ff3d57', gr:'rgba(255,255,255,0.4)' };

function lienMeta(l) {
  if (l === 'RADIO') return { color: C.or, label: 'RADIO (secours)' };
  if (l === 'WIFI')  return { color: C.vt, label: 'WiFi' };
  if (l === '2G')    return { color: C.bl, label: '2G Airtel' };
  return { color: C.gr, label: l || '—' };
}
function djMeta(d) {
  if (!d || d === 'inconnu') return { color: C.gr, label: 'inconnu' };
  if (/ferm/i.test(d))   return { color: C.vt, label: d };
  if (/ouvert/i.test(d)) return { color: C.rg, label: d };
  return { color: C.or, label: d };
}
function depuis(ms) {
  if (ms == null) return '—';
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `il y a ${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min ${s % 60} s`;
  return `il y a ${Math.floor(m/60)} h ${m % 60} min`;
}

const panel = (children, extra = {}) => (
  <div style={{ background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:8, padding:'12px 14px', ...extra }}>{children}</div>
);
const title = (t) => (
  <div style={{ fontFamily:'var(--font-sans)', fontSize:9, letterSpacing:3, textTransform:'uppercase', color:'rgba(255,255,255,0.3)', marginBottom:10 }}>{t}</div>
);

export default function App() {
  const [passerelles, setPasserelles] = useState({});   // id -> état
  const [packets, setPackets]         = useState([]);
  const uptime = useUptime();
  const now = useNow();

  const onMessage = useCallback((msg) => {
    const raw = (msg.raw || '').trim();
    if (!raw) return;
    const rxTime = msg.timestamp || Date.now();
    const p = parsePasserelle(raw);
    if (p) {
      setPasserelles(prev => ({
        ...prev,
        [p.id]: {
          ...p,
          rssi: (msg.rssi != null ? msg.rssi : prev[p.id]?.rssi) ?? null,
          ip: (msg.deviceIp || '').replace('::ffff:', '') || prev[p.id]?.ip || null,
          lastSeen: rxTime,
        },
      }));
    }
    setPackets(prev => [{
      uid: ++uidCounter, time: formatTime(new Date(rxTime)),
      seq: p?.seq ?? null, raw, valid: !!p,
    }, ...prev].slice(0, MAX_PACKETS));
  }, []);

  const { state: wsState, deviceCount, devices } = useWebSocket(onMessage);

  const liste = Object.values(passerelles)
    .sort((a, b) => (a.quartier || a.id).localeCompare(b.quartier || b.id));
  const base = devices?.[0];

  const connRows = [
    { label:'WebSocket',        val: wsState, color: wsState === CONNECTION_STATE.CONNECTED ? C.vt : C.rg },
    { label:'Stations de base', val:`${deviceCount}`, color: deviceCount > 0 ? C.vt : C.rg },
    { label:'Serveur',          val:'dihp.aderm.link /ingest', color: C.gr },
    { label:'RSSI station',     val: base?.rssi ? `${base.rssi} dBm` : '—', color: base?.rssi >= -60 ? C.vt : C.or },
    { label:'IP station',       val: base?.ip?.replace('::ffff:','') || '—', color: C.gr },
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', minHeight:'100vh', background:'var(--bg)' }}>
      <AppHeader wsState={wsState}/>
      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:10, padding:'12px 16px 16px' }}>

        {/* Bandeau résumé */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:8 }}>
          {[
            { label:'Passerelles vues', val: liste.length, color: C.or },
            { label:'En secours radio', val: liste.filter(p => p.lien === 'RADIO').length, color: C.or },
            { label:'Stations de base', val: deviceCount, color: deviceCount > 0 ? C.vt : C.rg },
            { label:'Uptime tableau',   val: uptime, color: C.bl },
          ].map(s => panel(
            <div key={s.label}>
              <div style={{ fontFamily:'var(--font-sans)', fontSize:9, letterSpacing:2, textTransform:'uppercase', color:'rgba(255,255,255,0.3)' }}>{s.label}</div>
              <div style={{ fontFamily:'var(--font-mono)', fontSize:22, color:s.color, marginTop:4 }}>{s.val}</div>
            </div>, { padding:'10px 14px' }
          ))}
        </div>

        {/* Cartes passerelles */}
        {liste.length === 0
          ? panel(<div style={{ textAlign:'center', color:'rgba(255,255,255,0.2)', fontFamily:'var(--font-mono)', fontSize:12, padding:'30px 0' }}>En attente d'un compte rendu de passerelle…</div>)
          : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:10 }}>
              {liste.map(p => {
                const lm = lienMeta(p.lien), dm = djMeta(p.dj);
                const age = now - (p.lastSeen || 0);
                const perdu = age > 30000;
                return (
                  <div key={p.id} style={{ background:'rgba(0,0,0,0.3)', border:`1px solid ${perdu ? 'rgba(255,61,87,0.4)' : lm.color+'55'}`, borderRadius:8, padding:'12px 14px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                      <span style={{ fontFamily:'var(--font-sans)', fontSize:15, fontWeight:700, color:'#fff' }}>{p.quartier || p.id}</span>
                      <span style={{ fontFamily:'var(--font-sans)', fontSize:9, letterSpacing:1, padding:'2px 7px', borderRadius:10, color:lm.color, background:lm.color+'1a', border:`1px solid ${lm.color}55` }}>{lm.label}</span>
                    </div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'rgba(255,255,255,0.35)', marginTop:2 }}>{p.id}</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 10px', marginTop:10 }}>
                      {[
                        { k:'Disjoncteur', v:dm.label, c:dm.color },
                        { k:'Uptime carte', v:p.up || '—', c:'rgba(255,255,255,0.7)' },
                        { k:'N° rapport', v:p.seq != null ? `#${p.seq}` : '—', c:'rgba(255,255,255,0.7)' },
                        { k:'Dernier', v:depuis(age), c: perdu ? C.rg : 'rgba(255,255,255,0.7)' },
                      ].map(x => (
                        <div key={x.k}>
                          <div style={{ fontFamily:'var(--font-sans)', fontSize:8, letterSpacing:1, textTransform:'uppercase', color:'rgba(255,255,255,0.25)' }}>{x.k}</div>
                          <div style={{ fontFamily:'var(--font-mono)', fontSize:12, color:x.c }}>{x.v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        {/* Bas : journal + connexion */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:10 }}>
          {panel(<>
            {title('Trames reçues (brut)')}
            <div style={{ maxHeight:220, overflowY:'auto', fontFamily:'var(--font-mono)', fontSize:11 }}>
              {packets.length === 0 ? <div style={{ color:'rgba(255,255,255,0.15)' }}>—</div> :
                packets.map(e => (
                  <div key={e.uid} style={{ display:'flex', gap:8, padding:'3px 0', borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                    <span style={{ color:'rgba(255,255,255,0.25)' }}>{e.time}</span>
                    <span style={{ color: e.valid ? C.vt : C.rg }}>{e.valid ? '✓' : '✗'}</span>
                    <span style={{ color:'rgba(255,255,255,0.6)', wordBreak:'break-all' }}>{e.raw}</span>
                  </div>
                ))}
            </div>
          </>)}
          {panel(<>
            {title('Connexion VPS')}
            {connRows.map(r => (
              <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ fontFamily:'var(--font-sans)', fontSize:9, letterSpacing:1.5, textTransform:'uppercase', color:'rgba(255,255,255,0.25)' }}>{r.label}</span>
                <span style={{ fontFamily:'var(--font-mono)', fontSize:11, color:r.color }}>{r.val}</span>
              </div>
            ))}
          </>)}
        </div>
      </div>
    </div>
  );
}
